<template>
  <div id="broadcasting"></div>
</template>

<script>
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { normalizeServerUrl } from "@/utils/urlHelper.js";

export default {
  name: "Broadcasting",
  data() {
    return {
      echo: null,
      connectionState: "disconnected", // disconnected | connecting | connected | reconnecting | failed
    };
  },

  computed: {
    loggedIn: function () {
      return this.$store.getters["auth/isLoggedIn"];
    },
    token: function () {
      return this.$store.getters["auth/userToken"];
    },
    isConnected: function () {
      return this.connectionState === "connected";
    },
  },
  watch: {
    loggedIn: {
      handler: function (loggedIn) {
        if (loggedIn) {
          this.connect();
        } else {
          this.disconnect();
        }
      },
    },
    connectionState(newState, oldState) {
      this.$emit("stateChange", newState);
      if (newState === "connected") {
        this.$emit("socketStatus", this.echo);
        if (oldState === "reconnecting") {
          this.$emit("reconnected");
        }
      } else if (newState === "disconnected" || newState === "reconnecting") {
        this.$emit("connectionLost");
      }
    },
  },
  mounted() {
    if (this.loggedIn) {
      this.connect();
    }
    window.addEventListener("online", this.handleOnline);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  beforeDestroy() {
    window.removeEventListener("online", this.handleOnline);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  },
  methods: {
    connect() {
      if (!this.echo) {
        this.connectionState = "connecting";
        // Use ws:// for localhost, wss:// for production
        const isLocalhost =
          process.env.VUE_APP_WSHOST === "localhost" ||
          process.env.VUE_APP_WSHOST === "127.0.0.1";
        const useEncryption = !isLocalhost;

        this.echo = new Echo({
          broadcaster: "pusher",
          key: process.env.VUE_APP_PUSHER_APP_KEY,
          cluster: process.env.VUE_APP_PUSHER_APP_CLUSTER,
          encrypted: useEncryption,
          wsHost: process.env.VUE_APP_WSHOST,
          wsPort: process.env.VUE_APP_WSPORT,
          wssHost: process.env.VUE_APP_WSHOST,
          wssPort: process.env.VUE_APP_WSPORT,
          disableStats: true,
          enabledTransports: ["ws", "wss"],
          forceTLS: useEncryption,
          activityTimeout: 120000,
          pongTimeout: 30000,
          authEndpoint:
            normalizeServerUrl(process.env.VUE_APP_DEVSERVER) +
            "/broadcasting/auth",
          auth: {
            headers: {
              Authorization: null,
            },
          },
        });

        const connection = this.echo.connector.pusher.connection;

        connection.bind("connected", () => {
          console.log("[broad] : Websocket Connected Successfully");
          this.connectionState = "connected";
        });

        connection.bind("connecting", () => {
          if (this.connectionState === "connected") {
            this.connectionState = "reconnecting";
          }
        });

        connection.bind("unavailable", () => {
          console.warn("[broad] : Websocket connection unavailable");
          if (this.connectionState === "connected") {
            this.connectionState = "reconnecting";
          }
        });

        connection.bind("disconnected", () => {
          console.log("[broad] : Websocket Disconnected");
          // Do NOT call this.disconnect() here — let Pusher handle automatic reconnection.
          // Only update state so the UI can react.
          if (this.connectionState !== "disconnected") {
            this.connectionState = "reconnecting";
          }
        });

        connection.bind("error", (error) => {
          console.error("[broad] : Websocket Connection error:", error);
        });

        connection.bind("failed", () => {
          console.error(
            "[broad] : Websocket Connection failed — will not retry automatically"
          );
          this.connectionState = "failed";
        });
      }

      // Always update the auth token before connecting
      this.echo.connector.options.auth.headers.Authorization = this.token;
      this.echo.connector.pusher.connect();
    },

    disconnect() {
      if (!this.echo) {
        return;
      }
      this.connectionState = "disconnected";
      this.echo.disconnect();
      this.$emit("logout");
    },

    handleOnline() {
      // Browser came back online — nudge Pusher to reconnect if we should be connected
      if (this.loggedIn && this.echo && this.connectionState !== "connected") {
        console.log("[broad] : Network online — reconnecting WebSocket");
        this.connectionState = "reconnecting";
        this.echo.connector.pusher.connect();
      }
    },

    handleVisibilityChange() {
      // Tab became visible again — check connection health
      if (
        document.visibilityState === "visible" &&
        this.loggedIn &&
        this.echo
      ) {
        const pusherState = this.echo.connector.pusher.connection.state;
        if (pusherState !== "connected") {
          console.log(
            "[broad] : Tab visible, Pusher state:",
            pusherState,
            "— reconnecting"
          );
          this.connectionState = "reconnecting";
          this.echo.connector.pusher.connect();
        } else {
          // Connection looks alive — emit reconnected so App.vue can refresh stale data
          this.$emit("tabResumed");
        }
      }
    },
  },
};
</script>
